import { useDocumentVisibility, useWindowEvent } from "@mantine/hooks";
import { useEffect, useRef }                     from "react";

import { useGetIsPlaying }        from "_components/features/player/states";
import { useRTCWebSocketContext } from "_components/features/rtcSocket";

export function useLSWatch ( {
	broadcastId,
	streamHasEnded,
	interval,
	isSessionEnded
} ) {

	const viewStartTS     = useRef ();
	const startWatchTS    = useRef ();
	const currentWatchTS  = useRef ();
	const firstView       = useRef ( true );
	const documentState   = useDocumentVisibility ();
	const { sendMessage } = useRTCWebSocketContext ();
	const isPlaying       = useGetIsPlaying ();

	const triggerSendWatchTime = async ( visible = true ) => {
		currentWatchTS.current = new Date ();

		/* Note:
            viewStart    : the timestamp when the user starts viewing
            relativeTime : the cursor position relative to the start
            watchStart   : the timestamp of starting this watch
            watchEnd     : the timestamp of ending the watch
        */

		const relativeTime = ( currentWatchTS?.current?.getTime () - viewStartTS?.current?.getTime () ) / 1000;

		const data = {
			liveSessionId : +broadcastId,
			viewStart     : viewStartTS.current?.getTime (),
			relativeTime  : parseInt ( relativeTime ),
			watchStart    : startWatchTS.current?.getTime (),
			watchEnd      : currentWatchTS.current?.getTime (),
			isLive        : isSessionEnded ? false : true,
			isAutoplay    : false,
			webVisibility : visible ? 1 : 0
		};

		sendMessage ( { data: { data: { watch: [ data ] } }, event: "lsWatch" } );

		startWatchTS.current = currentWatchTS.current;
	};

	useWindowEvent ( "beforeunload", () => {
		triggerSendWatchTime ( true );
		clearInterval ( interval.current );
	} );

	const startIsLiveTimer = visible => {
		const duration = 60000; // Calling after every 60 * 1000 milliseconds

		interval.current = setInterval ( () => {
			triggerSendWatchTime ( visible );
		}, duration );
	};

	useEffect ( () => {
		if ( !broadcastId || !isPlaying ) {
			clearInterval ( interval.current );

			return;
		}

		let nowTime = new Date ();

		viewStartTS.current    = nowTime;
		startWatchTS.current   = nowTime;
		streamHasEnded.current = false;

		startIsLiveTimer ( true );

		return () => {
			clearInterval ( interval.current );
		};
	}, [ broadcastId, isPlaying ] );

	useEffect ( () => {
		if ( streamHasEnded.current )
			return;

		if ( !firstView.current && documentState === "visible" ) {
			clearInterval ( interval.current );
			triggerSendWatchTime ( false );
			startIsLiveTimer ( true );
		}

		if ( documentState === "hidden" ) {
			clearInterval ( interval.current );
			triggerSendWatchTime ( true );
			startIsLiveTimer ( false );
		}

		firstView.current = false;
	}, [ documentState ] );

	return null;
}
